import React, { useEffect, useRef, useState } from 'react';
import { useQuery, useReactiveVar } from '@apollo/client';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Dropdown,
  Grid,
  Toggle,
  Typography,
} from '@statsbomb/kitbag-components';
import { useTheme } from 'styled-components';
import SnapDistributionChart from './SnapDistributionChart';
import { GET_SNAP_DISTRIBUTIONS } from './getSnapDistributions';
import { GET_PLAYER_PATHS } from './getPlayerPaths';
import { formatSnapData, snapsFromZone } from './PlayerSnap.dataManipulation';
import Tile from '../../../components/Tile/Tile';
import useQueryString from '../../../utils/hooks/useQueryString';
import {
  mf_PlayerDetails,
  mf_TeamDetails,
  mf_LeagueLevel,
  mf_Players,
} from '../../../apollo';
import PlayerRoutesChart from './PlayerRoutesChart';
import {
  addRouteInfo,
  ROUTES_Y_MODES,
  ROUTES_Y_OPTIONS,
} from './PlayerRoutesSummary.dataManipulation';
import { ROTATIONS, ROTATION_OPTIONS } from '../../../utils/constants/charting';
import TableTile from '../../../components/TableTile/TableTile';
import { INFO_ITEM_HEADERS, snapDistroInfo } from './SnapInfo';
import PlayerRoutesSummary from './PlayerRoutesSummary';
import KeyButton from '../../../components/buttons/DisplayKey/DisplayKey';
import ExportModal from '../../../components/ExportModal/ExportModal';
import { useExportSeasonInfo } from '../../../utils/hooks/useExportSeasonInfo';
import { escapeDownloadString } from '../../../utils/helpers/strings';
import {
  DOT_COLOR_MODE,
  DOT_COLOR_MODES,
  ROUTE_COLOR_MODES,
  HEATMAP_COLOR_MODES,
  HEATMAP_COLOR_MODE_TYPES,
} from './SnapDistributionChart.constants';
import { ROSTER_POSITIONS } from '../../../utils/constants/positions';
import {
  FixedAside,
  FixedAsideContent,
  GridBackground,
  KitbagPageGridHolder,
} from '../../../components/Layout/Layout.styles';
import AccordionTile from '../../../components/Accordion/AccordionTile';
import PageHeader from '../../../components/PageHeader/PageHeader';
import { DropdownOverride } from '../../../components/DropdownFilter/DropdownFilter.styles';
import { useStore } from '../../../store/useStore';
import {
  playerDetailsPropTypes,
  teamDetailsPropTypes,
} from '../../../apollo/index.data.propTypes';
import { PLAYER_SNAP_SIDEPANEL_WIDTH } from './PlayerSnap.constants';
import {
  getPlayerSnapCardState,
  getRouteCardState,
} from './PlayerSnap.helpers';
import { CardButtons, CardHeightWrapper } from '../../../components/Card/card';
import PlaylistButton from '../../../components/buttons/Playlist/Playlist';
import PassSelectedEvent from '../PlayerPassing/PassSelectedEvent';
import { MemoizedVideoTile } from '../../../components/Video/VideoTile';
import {
  formatPassingVideoData,
  getCurrentPlayData,
} from '../PlayerPassing/PlayerPassing.dataManipulation';
import { getSelectedEventDetails } from '../PlayerPassing/PassInfo';
import { getEnrichedTheme } from '../../../utils/visualisations/visPalettes';

const PlayerSnap = ({ __storybookPlayerDetails, __storybookTeamDetails }) => {
  // global state
  const competitionLevel = useReactiveVar(mf_LeagueLevel);
  const mf_TeamDetailsRV = useReactiveVar(mf_TeamDetails);
  const mf_PlayerDetailsRV = useReactiveVar(mf_PlayerDetails);
  const mf_PlayerRV = useReactiveVar(mf_Players);

  // page filters
  const [displayKey, setDisplayKey] = useState(true);
  // routes options
  const [routesDirection, setRoutesDirection] = useQueryString(
    'routes-direction',
    ROTATIONS.VERTICAL_UP
  );
  const [selectedYMode, setSelectedYMode] = useState(ROUTES_Y_MODES.FIELD);
  const [selectedZones, setSelectedZones] = useState(null);
  const [routeShowPaths, setRouteShowPaths] = useState(true);
  const [routeShowDots, setRouteShowDots] = useState(false);
  const [routeShowHeat, setRouteShowHeat] = useState(false);
  const [routeColorMode, setRouteColorMode] = useState(DOT_COLOR_MODE.GHOST);
  const [selectedPlay, setSelectedPlay] = useState(null);

  const [snapDirection, setSnapDirection] = useQueryString(
    'snap-direction',
    ROTATIONS.VERTICAL_UP
  );
  const [st_SnapY, setSnapY] = useQueryString('snap', false);
  const [st_PositionX, setPositionX] = useQueryString('pos', true);
  const [st_ColorMode, setColorMode] = useQueryString(
    'color',
    DOT_COLOR_MODES[0].value
  );
  const [st_HeatmapColorMode, setHeatmapColorMode] = useQueryString(
    'heatmap',
    HEATMAP_COLOR_MODE_TYPES.INDEPENDENT
  );

  // chart data
  const { loading, error, data } = useQuery(GET_SNAP_DISTRIBUTIONS);
  // paths data
  const {
    loading: loadingPaths,
    error: errorPaths,
    data: dataPaths,
  } = useQuery(GET_PLAYER_PATHS);

  // exporting
  const info1 = mf_TeamDetailsRV?.name;
  const { competitionSeason: info2, seasonName } = useExportSeasonInfo({
    asObject: true,
  });
  const infoDownloadName = `${escapeDownloadString(
    mf_PlayerDetailsRV?.name
  )} Snap Distribution Info ${seasonName}.csv`;

  const handleSnapY = () => {
    setSelectedZones(null);
    setSnapY(!st_SnapY);
  };

  const playerPosition =
    mf_PlayerDetailsRV?.mostCommonPosition?.generalPosition;

  // auto set initial orientation depending on whether a player is attack or defense
  useEffect(() => {
    const isDefensivePlayer = !ROSTER_POSITIONS[playerPosition]?.isOffense;
    const orientation = isDefensivePlayer
      ? ROTATIONS.VERTICAL_DOWN
      : ROTATIONS.VERTICAL_UP;

    setRoutesDirection(orientation);
    setSnapDirection(orientation);
  }, [mf_PlayerRV, playerPosition]);

  // clean up the data
  const snapData = data?.snapEvents?.edges.map((m) =>
    formatSnapData(m, competitionLevel)
  );
  const playerName =
    snapData?.length > 0 ? snapData[0].freezeFrames[0].player?.name : '';
  const teamName = snapData?.length > 0 ? `(${mf_TeamDetailsRV?.name})` : '';
  const positionAndName = snapData && `${playerName} ${teamName}`;
  const pathsData = dataPaths?.playerPlays?.edges.map((d) => d.node);
  const routedSnapData =
    snapData &&
    pathsData &&
    snapData?.map((p) => addRouteInfo(p, pathsData, selectedYMode));

  const zonedData =
    selectedZones === null
      ? routedSnapData
      : snapsFromZone(
          routedSnapData,
          st_SnapY,
          selectedZones,
          competitionLevel
        );
  const zonedDataPlayIds = zonedData && zonedData.map((m) => m.play.id);

  const mergedData = routedSnapData?.map((m) => ({
    ...m,
    inSelectedZone: zonedDataPlayIds
      ? zonedDataPlayIds.includes(m.play.id)
      : true,
  }));

  const infoItems = snapDistroInfo(mergedData);
  const zonedInfoItems = selectedZones && snapDistroInfo(zonedData);

  const selectedEventObj =
    mergedData &&
    selectedPlay &&
    mergedData?.find((f) => f.play.id === selectedPlay);

  const marginOverride =
    routesDirection === 'routes-direction'
      ? { top: 0, bottom: 0 }
      : { right: 0, left: 0 };

  const isHorizontal = snapDirection === ROTATIONS.HORIZONTAL;

  const isChartReady =
    positionAndName && !loading && !error && !!data.snapEvents.totalCount;

  const isVideoVisible = useStore((state) => state.snapDistributionVideoOpen);
  const setSelectedEvent = useStore(
    (state) => state.setSnapDistributionSelectedEvent
  );
  const selectedEvent = useStore(
    (state) => state.snapDistributionSelectedEvent
  );
  const setIsVideoVisible = useStore(
    (state) => state.setSnapDistributionVideoOpen
  );
  const videoActive = useRef(false);

  // Calculate grid column sizes based on video visibility and orientation
  const getSnapChartColumnSize = () => {
    if (isVideoVisible) return 12;
    return isHorizontal ? 8 : 6;
  };

  const getRouteChartColumnSize = () => {
    if (isVideoVisible) return 12;
    return isHorizontal ? 4 : 6;
  };

  // player colours for highlighting
  const theme = useTheme();
  const enrichedTheme = getEnrichedTheme(theme);
  const visPaletteEnriched = enrichedTheme.colours.visualisations;

  // callback from video player playlist hook
  const onPlaylistChange = (currentlyPlayingPlay) => {
    if (zonedDataPlayIds?.length > 0) {
      setSelectedEvent(
        data?.snapEvents?.edges?.find(
          (event) => event?.node?.play?.id === currentlyPlayingPlay?.id
        )?.node
      );
    }
  };

  // returns array of event objects
  const getVideoTileData = () => {
    // if the user has selected
    if (zonedDataPlayIds?.length > 0 && videoActive.current === false) {
      const filteredEvents = data.snapEvents.edges.map((edge) => edge.node);
      return formatPassingVideoData(filteredEvents, visPaletteEnriched);
    }
    // if the users clicks play all (typically on first load) without any active drag or selection
    if (
      data &&
      !selectedEvent &&
      zonedDataPlayIds?.length === 0 &&
      videoActive.current
    ) {
      const filteredEvents = data.snapEvents.edges.map((edge) => edge.node);
      return formatPassingVideoData(filteredEvents, visPaletteEnriched);
    }
    return zonedDataPlayIds;
  };

  return (
    <KitbagPageGridHolder>
      <Grid container={false} page>
        <FixedAside
          $width={`${!isVideoVisible ? PLAYER_SNAP_SIDEPANEL_WIDTH : 0}px`}
          $padding="1rem 0 6.5rem 0"
          $paddingTopMD="1rem"
        >
          <GridBackground>
            <AccordionTile
              id="snap-distribution-settings-accordion"
              header={
                <Tile.AccordionHeader>
                  Snap Alignment Settings
                </Tile.AccordionHeader>
              }
              body={
                <Tile.AccordionBody
                  $gap="0.5rem"
                  style={{ margin: `0 0 ${theme.sizing.tiny} 0` }}
                >
                  <Toggle
                    id="show-lateral-position-toggle"
                    label="Show lateral position relative to Snap"
                    onChange={handleSnapY}
                    checked={st_SnapY}
                    size="small"
                  />
                  <Toggle
                    id="show-depth-position-toggle"
                    label="Show Depth from Line of Scrimmage"
                    onChange={() => setPositionX(!st_PositionX)}
                    checked={st_PositionX}
                    size="small"
                  />
                  <DropdownOverride>
                    <Dropdown
                      id="snap-direction-dropdown"
                      options={ROTATION_OPTIONS}
                      label="Snap Alignment Direction"
                      onChange={(selectedOption) =>
                        setSnapDirection(selectedOption.value)
                      }
                      value={ROTATION_OPTIONS.find(
                        (f) => f.value === snapDirection
                      )}
                      isSearchable={false}
                      menuPosition="static"
                      size="small"
                    />
                  </DropdownOverride>
                  <DropdownOverride>
                    <Dropdown
                      id="dot-colouring-dropdown"
                      options={DOT_COLOR_MODES}
                      label="Dot Coloring"
                      onChange={(selectedOption) =>
                        setColorMode(selectedOption.value)
                      }
                      value={DOT_COLOR_MODES.find(
                        (f) => f.value === st_ColorMode
                      )}
                      isSearchable={false}
                      menuPosition="static"
                      size="small"
                    />
                  </DropdownOverride>
                  <DropdownOverride>
                    <Dropdown
                      id="heatmap-colouring-dropdown"
                      options={HEATMAP_COLOR_MODES}
                      label="Heatmap Intensity Scaling"
                      onChange={(selectedOption) =>
                        setHeatmapColorMode(selectedOption.value)
                      }
                      value={HEATMAP_COLOR_MODES.find(
                        (f) => f.value === st_HeatmapColorMode
                      )}
                      isSearchable={false}
                      menuPosition="static"
                      size="small"
                    />
                  </DropdownOverride>
                </Tile.AccordionBody>
              }
              isExpandedDefault
              saveState
            />

            <AccordionTile
              id="route-settings-accordion"
              header={
                <Tile.AccordionHeader>Routes Settings</Tile.AccordionHeader>
              }
              body={
                <Tile.AccordionBody
                  $gap="0.5rem"
                  style={{ margin: `0 0 ${theme.sizing.small} 0` }}
                >
                  <DropdownOverride>
                    <Dropdown
                      id="routes-field-rotation-dropdown"
                      options={ROTATION_OPTIONS}
                      label="Routes Field Direction"
                      onChange={(selectedOption) =>
                        setRoutesDirection(selectedOption.value)
                      }
                      value={ROTATION_OPTIONS.find(
                        (f) => f.value === routesDirection
                      )}
                      isSearchable={false}
                      menuPosition="static"
                      size="small"
                    />
                  </DropdownOverride>
                  <DropdownOverride>
                    <Dropdown
                      id="routes-y-mode-dropdown"
                      options={ROUTES_Y_OPTIONS}
                      label="Draw Routes"
                      onChange={(selectedOption) =>
                        setSelectedYMode(selectedOption.value)
                      }
                      value={ROUTES_Y_OPTIONS.find(
                        (f) => f.value === selectedYMode
                      )}
                      isSearchable={false}
                      menuPosition="static"
                      size="small"
                    />
                  </DropdownOverride>
                  <DropdownOverride>
                    <Dropdown
                      id="routes-color-mode-dropdown"
                      options={ROUTE_COLOR_MODES}
                      label="Path Coloring"
                      onChange={(selectedOption) =>
                        setRouteColorMode(selectedOption.value)
                      }
                      value={ROUTE_COLOR_MODES.find(
                        (f) => f.value === routeColorMode
                      )}
                      isSearchable={false}
                      menuPosition="static"
                      size="small"
                    />
                  </DropdownOverride>
                  <Toggle
                    id="routes-show-paths-toggle"
                    label="Show Paths"
                    onChange={() => setRouteShowPaths(!routeShowPaths)}
                    checked={routeShowPaths}
                    size="small"
                  />
                  <Toggle
                    id="routes-show-dots-toggle"
                    label="Show Origin Dots in Route Chart"
                    onChange={() => setRouteShowDots(!routeShowDots)}
                    checked={routeShowDots}
                    size="small"
                  />
                  <Toggle
                    id="routes-show-heat-toggle"
                    label="Show Snap Heat Map in Route Chart"
                    onChange={() => setRouteShowHeat(!routeShowHeat)}
                    checked={routeShowHeat}
                    size="small"
                  />
                </Tile.AccordionBody>
              }
              isExpandedDefault
              saveState
            />
          </GridBackground>
        </FixedAside>
        <FixedAsideContent
          $margin="0.5rem 0.5rem 0 0"
          $marginMd={`0.5rem 0.5rem -3.5px ${
            !isVideoVisible ? PLAYER_SNAP_SIDEPANEL_WIDTH - 2 : 0
          }px`}
        >
          <Grid container={false}>
            <Grid item xs={isVideoVisible ? 6 : 12}>
              <PageHeader
                href="/player/overview/:leagues/:seasons/:teams/:players"
                rootPage="Player"
                activePage="Snap Alignment"
                __storybookPlayerDetails={__storybookPlayerDetails}
                __storybookTeamDetails={__storybookTeamDetails}
              >
                <div />
              </PageHeader>
              <Grid>
                <Grid item xs={getSnapChartColumnSize()}>
                  <CardHeightWrapper $margin="0.5rem 0 -0.45rem 0">
                    <Card>
                      <CardHeader>
                        <Typography
                          variant="headingMedium"
                          style={{ paddingLeft: theme.sizing.small }}
                        >
                          Snap Alignment Chart
                        </Typography>
                        <CardButtons>
                          {zonedDataPlayIds?.length > 0 &&
                            selectedZones?.length > 0 && (
                              <Button
                                onClick={() => {
                                  setSelectedZones(null);
                                  videoActive.current = false;
                                  setIsVideoVisible(false);
                                }}
                                variant="secondary"
                                size="small"
                                title="Clear Selection"
                              >
                                Clear Selection
                              </Button>
                            )}
                          <PlaylistButton
                            handleClick={() => {
                              videoActive.current = true;
                              setIsVideoVisible(true);
                              // Set the first event as selected when opening video
                              const firstEvent =
                                zonedData?.[0] || snapData?.[0];
                              if (firstEvent) {
                                setSelectedEvent(firstEvent);
                              }
                            }}
                            numberOfPlays={
                              zonedDataPlayIds?.length > 0
                                ? zonedDataPlayIds.length
                                : data?.snapEvents?.edges?.length
                            }
                            isDisabled={
                              data?.snapEvents?.edges?.length === 0 ||
                              loading ||
                              isVideoVisible
                            }
                            variant={
                              zonedDataPlayIds?.length > 0 &&
                              selectedZones?.length > 0
                                ? 'primary'
                                : 'secondary'
                            }
                          />
                          <KeyButton
                            showKey={displayKey}
                            handleShowKey={() => setDisplayKey(!displayKey)}
                          />
                          <ExportModal
                            title={playerName}
                            secondaryTitle="Snap Alignment"
                            info1={info1}
                            info2={info2}
                            fileName={`${mf_PlayerDetailsRV?.name} Snap Alignment Chart ${seasonName}`}
                            isDisabled={!isChartReady}
                          >
                            <SnapDistributionChart
                              id="amf-snap-distribution-export"
                              data={mergedData}
                              summaryMode={false}
                              loading={loading}
                              showDy={st_SnapY}
                              showDx={st_PositionX}
                              orientation={snapDirection}
                              colorMode={st_ColorMode}
                              colorModeHeatmap={st_HeatmapColorMode}
                              selectedZones={selectedZones}
                              setSelectedZones={setSelectedZones}
                              showKey={displayKey}
                              margin={{
                                top: 0,
                                right: 0,
                                bottom: 20,
                                left: 0,
                              }}
                              isInteractive={false}
                            />
                          </ExportModal>
                        </CardButtons>
                      </CardHeader>
                      <CardBody
                        statusType={
                          getPlayerSnapCardState(loading, Boolean(error), data)
                            .state
                        }
                        statusTitle={
                          getPlayerSnapCardState(loading, Boolean(error), data)
                            .title
                        }
                        statusDescription={
                          getPlayerSnapCardState(loading, Boolean(error), data)
                            .description
                        }
                      >
                        <SnapDistributionChart
                          id="amf-snap-distribution"
                          data={mergedData}
                          summaryMode={false}
                          loading={loading}
                          showDy={st_SnapY}
                          showDx={st_PositionX}
                          orientation={snapDirection}
                          colorMode={st_ColorMode}
                          colorModeHeatmap={st_HeatmapColorMode}
                          selectedZones={selectedZones}
                          setSelectedZones={setSelectedZones}
                          showKey={displayKey}
                          margin={{
                            top: 0,
                            right: 1,
                            bottom: 16,
                            left: 0,
                          }}
                          selectedEvent={isVideoVisible ? selectedEvent : null}
                        />
                      </CardBody>
                    </Card>
                  </CardHeightWrapper>
                </Grid>
                <Grid item xs={getRouteChartColumnSize()}>
                  <CardHeightWrapper $margin="0.5rem 0 0 0">
                    <Card>
                      <CardHeader>
                        <Typography
                          variant="headingMedium"
                          style={{ paddingLeft: theme.sizing.small }}
                        >
                          Route Chart
                        </Typography>
                        <CardButtons>
                          <ExportModal
                            title={playerName}
                            secondaryTitle="Player Routes"
                            info1={info1}
                            info2={info2}
                            fileName={`${mf_PlayerDetailsRV?.name} Player Routes Chart ${seasonName}`}
                            isDisabled={!isChartReady}
                          >
                            <PlayerRoutesChart
                              id="amf-player-routes-chart-export"
                              data={zonedData}
                              orientation={routesDirection}
                              yMode={selectedYMode}
                              showPaths={routeShowPaths}
                              showDots={routeShowDots}
                              showHeatmap={routeShowHeat}
                              dotColorMode={st_ColorMode}
                              pathColorMode={routeColorMode}
                              selectedPlay={selectedPlay}
                              isInteractive={false}
                              marginOverride={marginOverride}
                            />
                          </ExportModal>
                        </CardButtons>
                      </CardHeader>
                      <CardBody
                        statusType={
                          getRouteCardState(
                            loadingPaths,
                            Boolean(errorPaths),
                            zonedData
                          ).state
                        }
                        statusTitle={
                          getRouteCardState(
                            loadingPaths,
                            Boolean(errorPaths),
                            zonedData
                          ).title
                        }
                        statusDescription={
                          getRouteCardState(
                            loadingPaths,
                            Boolean(errorPaths),
                            zonedData
                          ).description
                        }
                      >
                        <PlayerRoutesChart
                          id="amf-player-routes-chart"
                          data={zonedData}
                          orientation={routesDirection}
                          yMode={selectedYMode}
                          showPaths={routeShowPaths}
                          showDots={routeShowDots}
                          showHeatmap={routeShowHeat}
                          dotColorMode={st_ColorMode}
                          pathColorMode={routeColorMode}
                          selectedPlay={selectedPlay}
                          setSelectedPlay={setSelectedPlay}
                        />
                      </CardBody>
                    </Card>
                  </CardHeightWrapper>

                  {selectedEventObj && (
                    <PlayerRoutesSummary
                      fileName="Route Summary.csv"
                      snapDatum={selectedEventObj}
                      error={!!error || !!errorPaths}
                      loading={loading}
                    />
                  )}

                  {infoItems && infoItems?.length > 0 && (
                    <TableTile
                      data={infoItems}
                      tileTitle="Snap Information"
                      showColumnHeader
                      columnHeaders={INFO_ITEM_HEADERS}
                      error={!!errorPaths || !!error}
                      loading={loading}
                      fileName={infoDownloadName}
                      padding="0.25rem 1rem 1rem 1rem"
                      margin="0.5rem 0 0 0"
                      withBorder
                    />
                  )}

                  {zonedInfoItems && zonedInfoItems?.length > 0 && (
                    <TableTile
                      data={zonedInfoItems}
                      tileTitle="Snap Information - Selected Zones"
                      showColumnHeader
                      columnHeaders={INFO_ITEM_HEADERS}
                      error={!!errorPaths || !!error}
                      loading={loading}
                      fileName={infoDownloadName}
                      padding="0.25rem 1rem 1rem 1rem"
                      margin="0.5rem 0 0 0"
                      withBorder
                    />
                  )}
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={isVideoVisible ? 6 : 0}>
              <>
                {isVideoVisible && (
                  <MemoizedVideoTile
                    title={
                      getCurrentPlayData(getSelectedEventDetails(selectedEvent))
                        ?.title
                    }
                    subTitle={
                      getCurrentPlayData(getSelectedEventDetails(selectedEvent))
                        ?.game
                    }
                    data={getVideoTileData()}
                    // eslint-disable-next-line max-len
                    gotoLink={`/game/animation/:leagues/:seasons/:teams/${selectedEvent?.game?.id}/${selectedEvent?.play?.drive?.id}/${selectedEvent?.play?.id}`}
                    isGotoDisabled={
                      !selectedEvent?.game?.id ||
                      !selectedEvent?.play?.drive?.id ||
                      !selectedEvent?.play?.id
                    }
                    handlePlaylistChange={onPlaylistChange}
                    fileName="Snap Alignment Chart"
                    selectedPlayId={selectedEvent?.play?.id}
                    handleClose={() => {
                      videoActive.current = false;
                      setIsVideoVisible(false);
                    }}
                  >
                    <PassSelectedEvent
                      data={getCurrentPlayData(
                        getSelectedEventDetails(selectedEvent)
                      )}
                    />
                  </MemoizedVideoTile>
                )}
              </>
            </Grid>
          </Grid>
        </FixedAsideContent>
      </Grid>
    </KitbagPageGridHolder>
  );
};

PlayerSnap.propTypes = {
  __storybookPlayerDetails: playerDetailsPropTypes,
  __storybookTeamDetails: teamDetailsPropTypes,
};

export default PlayerSnap;
